/**
 * Customer Portal (UI) - account hierarchy navigator graph.
 */
import { useEffect, useMemo, useState } from 'react';
import { Canvas, Node, type EdgeData, type NodeData, type NodeProps } from 'reaflow';
import { TemplateAssignmentModal } from '../components/templates/TemplateAssignmentModal';
import { fetchBrands, fetchCampaigns, fetchPhoneNumbers } from '../features/api/client';
import type { Brand, Campaign, PhoneNumber, ScopeType } from '../types/portal';
import { createBrandAssignmentTarget } from './brands';
import { buildNavigatorGraph, parseNavigatorNodeId, type NavigatorGraphData, type NavigatorNodeType } from './navigator-graph';

type NavigatorRouteProps = {
  accountId: string | null;
};

type NavigatorSelection = {
  type: NavigatorNodeType;
  id: string;
};

type AssignmentTarget = {
  scopeType: ScopeType;
  scopeId: string;
  campaignId?: string | null;
  title: string;
};

export function NavigatorRoute({ accountId }: NavigatorRouteProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<NavigatorGraphData>({ nodes: [], edges: [] });
  const [brands, setBrands] = useState<Brand[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [selection, setSelection] = useState<NavigatorSelection | null>(null);
  const [brandTab, setBrandTab] = useState<'details' | 'campaigns' | 'numbers'>('details');
  const [campaignTab, setCampaignTab] = useState<'details' | 'numbers'>('details');
  const [assignmentTarget, setAssignmentTarget] = useState<AssignmentTarget | null>(null);

  useEffect(() => {
    if (!accountId) {
      setGraphData({ nodes: [], edges: [] });
      setBrands([]);
      setCampaigns([]);
      setPhoneNumbers([]);
      setSelection(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchBrands(accountId),
      fetchCampaigns(accountId),
      fetchPhoneNumbers(accountId, { brandId: null, campaignId: null, phoneNumberId: null })
    ])
      .then(([brandsResponse, campaignsResponse, phoneNumbersResponse]) => {
        if (cancelled) return;
        setBrands(brandsResponse.items);
        setCampaigns(campaignsResponse.items);
        setPhoneNumbers(phoneNumbersResponse.items);
        setGraphData(
          buildNavigatorGraph({
            brands: brandsResponse.items,
            campaigns: campaignsResponse.items,
            phoneNumbers: phoneNumbersResponse.items
          })
        );
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(requestError instanceof Error ? requestError.message : String(requestError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const hasHierarchy = useMemo(() => graphData.nodes.length > 0, [graphData.nodes.length]);
  const brandById = useMemo(() => new Map(brands.map((brand) => [brand.id, brand])), [brands]);
  const campaignById = useMemo(() => new Map(campaigns.map((campaign) => [campaign.id, campaign])), [campaigns]);
  const phoneById = useMemo(() => new Map(phoneNumbers.map((phoneNumber) => [phoneNumber.id, phoneNumber])), [phoneNumbers]);

  const selectedBrand = selection?.type === 'brand' ? brandById.get(selection.id) ?? null : null;
  const selectedCampaign = selection?.type === 'campaign' ? campaignById.get(selection.id) ?? null : null;
  const selectedPhone = selection?.type === 'phone' ? phoneById.get(selection.id) ?? null : null;

  const selectedBrandCampaigns = useMemo(
    () => (selectedBrand ? campaigns.filter((campaign) => campaign.brandId === selectedBrand.id) : []),
    [campaigns, selectedBrand]
  );
  const selectedBrandNumbers = useMemo(
    () => (selectedBrand ? phoneNumbers.filter((phoneNumber) => phoneNumber.brandId === selectedBrand.id) : []),
    [phoneNumbers, selectedBrand]
  );
  const selectedCampaignNumbers = useMemo(
    () => (selectedCampaign ? phoneNumbers.filter((phoneNumber) => phoneNumber.campaignId === selectedCampaign.id) : []),
    [phoneNumbers, selectedCampaign]
  );

  const selectedPhoneCampaign = useMemo(
    () => (selectedPhone ? campaignById.get(selectedPhone.campaignId) ?? null : null),
    [campaignById, selectedPhone]
  );

  function openSelection(nextSelection: NavigatorSelection): void {
    if (nextSelection.type === 'brand') setBrandTab('details');
    if (nextSelection.type === 'campaign') setCampaignTab('details');
    setSelection(nextSelection);
  }

  function handleGraphNodeClick(nodeId: string): void {
    const parsed = parseNavigatorNodeId(nodeId);
    if (!parsed) return;
    openSelection(parsed);
  }

  function renderGraphNode(nodeProps: NodeProps) {
    return (
      <Node
        {...nodeProps}
        onClick={(_event, data) => {
          handleGraphNodeClick(String(data.id ?? ''));
        }}
      />
    );
  }

  return (
    <>
      <div className="page-pretitle">Messaging</div>
      <h2 className="page-title mb-3">Navigator</h2>

      {!accountId ? <div className="alert alert-warning">Select an account to load the navigator graph.</div> : null}
      {error ? <div className="alert alert-danger">{error}</div> : null}
      {loading ? <div className="text-secondary">Loading navigator graph...</div> : null}

      {!loading && accountId ? (
        <div className="card">
          <div className="card-body">
            {!hasHierarchy ? (
              <div className="text-secondary">No brands, campaigns, or phone numbers were found for this account.</div>
            ) : (
              <div className="overflow-auto" style={{ maxWidth: '100%' }}>
                <div style={{ minWidth: 1200, height: 560 }}>
                  <Canvas
                    fit
                    direction="RIGHT"
                    pannable
                    zoomable
                    readonly
                    maxHeight={1600}
                    maxWidth={4000}
                    nodes={graphData.nodes as NodeData[]}
                    edges={graphData.edges as EdgeData[]}
                    node={renderGraphNode}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {selection?.type === 'brand' ? (
        <div className="modal modal-blur fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }} role="dialog" aria-modal="true">
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{selectedBrand?.name ?? `Brand ${selection.id}`}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setSelection(null)} />
              </div>
              <div className="modal-body">
                <ul className="nav nav-tabs mb-3">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${brandTab === 'details' ? 'active' : ''}`}
                      type="button"
                      onClick={() => setBrandTab('details')}
                    >
                      Details
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${brandTab === 'campaigns' ? 'active' : ''}`}
                      type="button"
                      onClick={() => setBrandTab('campaigns')}
                    >
                      Campaigns
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${brandTab === 'numbers' ? 'active' : ''}`}
                      type="button"
                      onClick={() => setBrandTab('numbers')}
                    >
                      Numbers
                    </button>
                  </li>
                </ul>

                {brandTab === 'details' ? (
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h3 className="card-title mb-0">Brand metadata</h3>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setAssignmentTarget(createBrandAssignmentTarget(selection.id, selectedBrand?.name))}
                      >
                        Assign templates
                      </button>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-vcenter card-table">
                        <tbody>
                          <tr>
                            <th className="w-25">Brand ID</th>
                            <td>{selectedBrand?.id ?? selection.id}</td>
                          </tr>
                          <tr>
                            <th className="w-25">Name</th>
                            <td>{selectedBrand?.name ?? 'Unknown brand'}</td>
                          </tr>
                          <tr>
                            <th className="w-25">Account ID</th>
                            <td>{selectedBrand?.accountId ?? accountId ?? '-'}</td>
                          </tr>
                          <tr>
                            <th className="w-25">Campaigns</th>
                            <td>{selectedBrandCampaigns.length}</td>
                          </tr>
                          <tr>
                            <th className="w-25">Phone numbers</th>
                            <td>{selectedBrandNumbers.length}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}

                {brandTab === 'campaigns' ? (
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title mb-0">Campaigns in this brand</h3>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-vcenter card-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Campaign ID</th>
                            <th className="w-1">Open</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedBrandCampaigns.map((campaign) => (
                            <tr key={campaign.id}>
                              <td>{campaign.name}</td>
                              <td className="text-secondary">{campaign.id}</td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => openSelection({ type: 'campaign', id: campaign.id })}
                                >
                                  View
                                </button>
                              </td>
                            </tr>
                          ))}
                          {!selectedBrandCampaigns.length ? (
                            <tr>
                              <td colSpan={3} className="text-secondary">
                                No campaigns found for this brand.
                              </td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}

                {brandTab === 'numbers' ? (
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title mb-0">Phone numbers in this brand</h3>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-vcenter card-table">
                        <thead>
                          <tr>
                            <th>Label</th>
                            <th>Number</th>
                            <th>Campaign</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedBrandNumbers.map((phoneNumber) => (
                            <tr key={phoneNumber.id} className="cursor-pointer" onClick={() => openSelection({ type: 'phone', id: phoneNumber.id })}>
                              <td>{phoneNumber.label}</td>
                              <td>{phoneNumber.e164}</td>
                              <td className="text-secondary">
                                {campaignById.get(phoneNumber.campaignId)?.name ?? phoneNumber.campaignId}
                              </td>
                            </tr>
                          ))}
                          {!selectedBrandNumbers.length ? (
                            <tr>
                              <td colSpan={3} className="text-secondary">
                                No phone numbers found for this brand.
                              </td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {selection?.type === 'campaign' ? (
        <div className="modal modal-blur fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }} role="dialog" aria-modal="true">
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{selectedCampaign?.name ?? `Campaign ${selection.id}`}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setSelection(null)} />
              </div>
              <div className="modal-body">
                <ul className="nav nav-tabs mb-3">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${campaignTab === 'details' ? 'active' : ''}`}
                      type="button"
                      onClick={() => setCampaignTab('details')}
                    >
                      Details
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${campaignTab === 'numbers' ? 'active' : ''}`}
                      type="button"
                      onClick={() => setCampaignTab('numbers')}
                    >
                      Numbers
                    </button>
                  </li>
                </ul>

                {campaignTab === 'details' ? (
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h3 className="card-title mb-0">Campaign metadata</h3>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() =>
                          setAssignmentTarget({
                            scopeType: 'CAMPAIGN',
                            scopeId: selection.id,
                            title: `Assign templates - ${selectedCampaign?.name ?? selection.id}`
                          })
                        }
                      >
                        Assign templates
                      </button>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-vcenter card-table">
                        <tbody>
                          <tr>
                            <th className="w-25">Campaign ID</th>
                            <td>{selectedCampaign?.id ?? selection.id}</td>
                          </tr>
                          <tr>
                            <th className="w-25">Name</th>
                            <td>{selectedCampaign?.name ?? 'Unknown campaign'}</td>
                          </tr>
                          <tr>
                            <th className="w-25">Account ID</th>
                            <td>{selectedCampaign?.accountId ?? accountId ?? '-'}</td>
                          </tr>
                          <tr>
                            <th className="w-25">Phone numbers</th>
                            <td>{selectedCampaignNumbers.length}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}

                {campaignTab === 'numbers' ? (
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title mb-0">Phone numbers</h3>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-vcenter card-table">
                        <thead>
                          <tr>
                            <th>Label</th>
                            <th>Number</th>
                            <th>ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedCampaignNumbers.map((phoneNumber) => (
                            <tr key={phoneNumber.id} className="cursor-pointer" onClick={() => openSelection({ type: 'phone', id: phoneNumber.id })}>
                              <td>{phoneNumber.label}</td>
                              <td>{phoneNumber.e164}</td>
                              <td className="text-secondary">{phoneNumber.id}</td>
                            </tr>
                          ))}
                          {!selectedCampaignNumbers.length ? (
                            <tr>
                              <td colSpan={3} className="text-secondary">
                                No phone numbers found for this campaign.
                              </td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {selection?.type === 'phone' ? (
        <div className="modal modal-blur fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }} role="dialog" aria-modal="true">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Phone number detail</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setSelection(null)} />
              </div>
              <div className="modal-body">
                <dl className="row mb-0">
                  <dt className="col-sm-4">Label</dt>
                  <dd className="col-sm-8">{selectedPhone?.label ?? 'Unknown'}</dd>
                  <dt className="col-sm-4">Number</dt>
                  <dd className="col-sm-8">{selectedPhone?.e164 ?? '-'}</dd>
                  <dt className="col-sm-4">Phone ID</dt>
                  <dd className="col-sm-8">{selectedPhone?.id ?? selection.id}</dd>
                  <dt className="col-sm-4">Campaign ID</dt>
                  <dd className="col-sm-8">{selectedPhone?.campaignId ?? '-'}</dd>
                  <dt className="col-sm-4">Campaign Name</dt>
                  <dd className="col-sm-8">{selectedPhoneCampaign?.name ?? '-'}</dd>
                </dl>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={() => setSelection(null)}>
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary ms-auto"
                  onClick={() =>
                    setAssignmentTarget({
                      scopeType: 'PHONE',
                      scopeId: selectedPhone?.e164 ?? '',
                      campaignId: selectedPhone?.campaignId ?? null,
                      title: `Assign templates - ${selectedPhone?.e164 ?? selection.id}`
                    })
                  }
                >
                  Assign templates
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <TemplateAssignmentModal
        isOpen={Boolean(assignmentTarget)}
        scopeType={assignmentTarget?.scopeType ?? 'ACCOUNT'}
        scopeId={assignmentTarget?.scopeId ?? accountId ?? ''}
        campaignId={assignmentTarget?.campaignId}
        title={assignmentTarget?.title ?? 'Assign templates'}
        onClose={() => setAssignmentTarget(null)}
      />
    </>
  );
}
